/** @jsxRuntime automatic */
import {
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { CircleAlert } from "lucide-react";
import { Collapse } from "./collapse";
import { cn } from "./utils";
import { Label } from "./label";
import { LazySwap } from "./swap-boot";

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  className?: string;
}

/**
 * A label, one control and its line under it (spec 07 › Resultados, «Error de
 * campo»): one slot for the hint or the error, never both. With no hint, the
 * error opens its line in the flow in 120 (Collapse) with its content
 * arriving, and closes in 80 then its rows. Over a hint it is replaced in
 * place (a panel Swap, `.ui-field__line`: the hint leaves in 80 and the error
 * fades in from 0 in 120 from +40, so the two never read on top of each
 * other; and back), and the slot's height follows the new line in 120
 * (Collapse `follow`), so what is below moves once, or not at all when both
 * lines are as tall; with reduced motion a 120 crossfade and the height at
 * once. The
 * red border is at once (the control's own `aria-invalid`). The line carries
 * its own spacing, so a closing one leaves no gap behind.
 */
export function Field({ label, hint, error, htmlFor, children, className }: FieldProps) {
  const generatedId = useId();
  const controlId = htmlFor ?? (typeof children.props.id === "string" ? children.props.id : generatedId);
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: controlId,
        "aria-invalid": error ? true : children.props["aria-invalid"],
        "aria-describedby": describedBy ?? children.props["aria-describedby"],
      })
    : children;

  // A new message over an error changes in place with no motion: it can follow each keystroke.
  const shown = error ? "error" : hint ? "hint" : null;
  const line =
    shown === "error" ? (
      // The error is never color alone: a 14 px alert icon leads it (components-01).
      <p id={errorId} role="alert" className="flex items-start gap-1.5 pt-1.5 text-sm text-destructive">
        <CircleAlert aria-hidden="true" className="mt-[3px] size-3.5 shrink-0" />
        <span className="min-w-0">{error}</span>
      </p>
    ) : (
      <p id={hintId} className="pt-1.5 text-sm text-muted-foreground">
        {hint}
      </p>
    );

  return (
    <div className={cn("grid", className)}>
      <div className="grid gap-1.5">
        <Label htmlFor={controlId}>{label}</Label>
        {control}
      </div>
      {/* A hint is there from the start; an error alone opens its line. The
          Swap loads after the mount, outside the login's boot (swap-boot.ts). */}
      <Collapse open={shown !== null} appear={hint ? "never" : "live"} follow className="data-[state=open]:duration-fast">
        <LazySwap swapKey={shown} variant="panel" className="ui-field__line" fallback={line}>
          {line}
        </LazySwap>
      </Collapse>
    </div>
  );
}
